// User Management Types

// User roles - matches database enum
export type UserRole = 'super_admin' | 'owner' | 'pipeline_manager' | 'team_lead' | 'team_member' | 'external_partner' | 'pending' | 'intern';

// User status - matches database enum
export type UserStatus = 'invited' | 'active' | 'deactivated';

// Project permission levels - matches database enum
export type ProjectPermission = 'view' | 'edit' | 'admin';

// User profile from database
export interface UserProfile {
  id: string;
  auth_user_id: string | null;
  email: string;
  display_name: string | null;
  role: UserRole;
  status: UserStatus;
  owner_id: string | null;
  invited_by: string | null;
  invited_at: string | null;
  invite_accepted_at: string | null;
  last_login_at: string | null;
  deactivated_at: string | null;
  intern_track: 'creative' | 'social' | 'research' | null;
  created_at: string;
  updated_at: string;
  // Included when fetched via API with joins
  invited_by_profile?: {
    email: string;
    display_name: string | null;
  } | null;
  owner?: {
    id: string;
    name: string;
  } | null;
}

// User profile with inviter details (for UI display)
export interface UserProfileWithInviter extends UserProfile {
  inviter?: {
    id: string;
    display_name: string | null;
    email: string;
  };
}

// Project access record from database
export interface ProjectAccess {
  id: string;
  user_profile_id: string;
  project_id: string;
  permission: ProjectPermission;
  can_view_financials: boolean;
  can_view_payments: boolean;
  can_view_team: boolean;
  granted_by: string | null;
  granted_at: string;
  expires_at: string | null;
  created_at: string;
}

// Project access with project details (for UI display)
export interface ProjectAccessWithProject extends ProjectAccess {
  project?: {
    id: string;
    name: string;
    status: string;
  };
}

// Admin notification from database
export interface AdminNotification {
  id: string;
  type: 'new_signup' | 'access_request' | string;
  title: string;
  message: string | null;
  related_user_id: string | null;
  is_read: boolean;
  read_at?: string | null;
  created_at: string;
  // Included when fetched via API with joins
  related_user?: {
    id: string;
    email: string;
    display_name: string | null;
    role: UserRole;
  } | null;
}

// Derived invite status for UI display
export type InviteStatus =
  | 'original'         // Original/seeded user (no invite_by, no invite_at)
  | 'self_signup'      // No invite, signed up on their own
  | 'invite_pending'   // Invited but hasn't clicked magic link yet
  | 'invite_accepted'; // Invited and has signed up

// Helper function to derive invite status from profile
export function getInviteStatus(profile: UserProfile): InviteStatus {
  // Original seeded users have no invited_by and no invited_at
  if (!profile.invited_by && !profile.invited_at) {
    // Check if they were created before the migration (original users)
    // Or if they self-signed up (will have a recent created_at after migration)
    // For simplicity, if role is not pending and no invite info, they're original
    if (profile.role !== 'pending') {
      return 'original';
    }
    return 'self_signup';
  }

  // Has invited_by but no invite_accepted_at = pending invite
  if (profile.invited_by && !profile.invite_accepted_at) {
    return 'invite_pending';
  }

  // Has both invited_by and invite_accepted_at = accepted invite
  if (profile.invited_by && profile.invite_accepted_at) {
    return 'invite_accepted';
  }

  // Fallback: self-signup (role is pending, no invite)
  return 'self_signup';
}

// Format invite status for display
export function formatInviteStatus(profile: UserProfile): string {
  const status = getInviteStatus(profile);

  switch (status) {
    case 'original':
      return '—';
    case 'self_signup':
      return 'Self-signup';
    case 'invite_pending':
      return 'Invite sent';
    case 'invite_accepted':
      return 'Accepted';
    default:
      return '—';
  }
}

// Role display labels
export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Admin',
  owner: 'Owner',
  pipeline_manager: 'Pipeline Manager',
  team_lead: 'Team Lead',
  team_member: 'Team Member',
  external_partner: 'External',
  pending: 'Pending',
  intern: 'Intern',
};

// Role badge variants mapped to Badge component variants
// 'default' = forest green (primary), 'warning' = amber/pending, 'secondary' = muted
// 'accent' is a custom className override for gold styling
export const ROLE_BADGE_VARIANTS: Record<UserRole, { variant: 'default' | 'secondary' | 'warning' | 'outline'; className?: string }> = {
  super_admin: { variant: 'default' },
  owner: { variant: 'default' },
  pipeline_manager: { variant: 'outline', className: 'bg-sky-50 text-sky-700 border-sky-200' },
  team_lead: { variant: 'outline', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  team_member: { variant: 'secondary' },
  external_partner: { variant: 'outline', className: 'bg-accent/15 text-accent border-accent/20' },
  pending: { variant: 'warning' },
  intern: { variant: 'outline', className: 'bg-amber-50 text-amber-700 border-amber-200' },
};

// Status display labels
export const STATUS_LABELS: Record<UserStatus, string> = {
  invited: 'Invited',
  active: 'Active',
  deactivated: 'Deactivated',
};

// API request/response types

export interface InviteUserRequest {
  email: string;
  role: UserRole;
  display_name?: string;
  project_access?: {
    project_id: string;
    permission: ProjectPermission;
    can_view_financials?: boolean;
    can_view_payments?: boolean;
    can_view_team?: boolean;
  }[];
}

export interface UpdateUserRequest {
  role?: UserRole;
  status?: UserStatus;
  display_name?: string;
}

export interface GrantProjectAccessRequest {
  project_id: string;
  permission: ProjectPermission;
  can_view_financials?: boolean;
  can_view_payments?: boolean;
  can_view_team?: boolean;
  expires_at?: string;
}
